import ClientCalendar from "@components/common/calendar";
import { ErrorAlert } from "@components/common/ErrorAlert";
import { useUserContext } from "@contexts/UserContext/index.context";
import { InfoOutlined, StarBorderPurple500Outlined } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, CircularProgress, Container, Grid, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { DeliveryStatus, DeliveryType } from "@types_def/models/delivery.types";
import { getDeliveryTypeColor } from "@utils/functions/Delivery/DeliveryType/deliveryTypeAttributes";
import { addHours, endOfDay, endOfMonth, parseISO, startOfDay, startOfMonth } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { useFindAllDeliveryQuery } from "@/libs/generated/hooks";
import type { FindAllDeliveryQuery } from "@/libs/generated/operations";

type Range = {
  start: Date;
  end: Date;
};

const today = new Date();
const firstDayOfMonth = startOfMonth(today);
const lastDayOfMonth = endOfMonth(today);

function CalendarView() {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const [range, setRange] = React.useState<Range>({
    start: firstDayOfMonth,
    end: lastDayOfMonth,
  });
  const [showDelivered, setShowDelivered] = React.useState(false);
  const organizationId = userState?.organizationId;

  const statusFilter = showDelivered
    ? [DeliveryStatus.DELIVERED, DeliveryStatus.COMPLETED]
    : [DeliveryStatus.SET, DeliveryStatus.CONFIRMED, DeliveryStatus.WAITING_FOR_DRIVER];

  const selectDeliveries = React.useCallback((response: FindAllDeliveryQuery) => {
    const records = response.findAllDelivery.records;
    return records.map((delivery) => {
      const fromAddress = delivery.fromAddress?.address ?? "";
      const toAddress = delivery.toAddress?.address ?? "";
      const toReturnToAddress = delivery.toReturnToAddress?.address;

      const typeLabel = delivery.type === DeliveryType.STANDARD ? "Standard" : "Restitution";
      const returnAddressPart =
        delivery.type === DeliveryType.TWO_WAY_DELIVERY
          ? `Arrivée Restitution: ${toReturnToAddress ?? "pas scpécifié"}\n`
          : "";

      const address =
        delivery.type === DeliveryType.STANDARD
          ? toAddress
          : (toReturnToAddress ?? "pas scpécifié");

      return {
        id: delivery.id,
        title: `Mission N°${delivery.id}\nType: ${typeLabel}\nDépart Livraison: ${fromAddress}\nArrivée Livraison: ${toAddress}\n${returnAddressPart}`,
        start: parseISO(delivery.carDeliveryDeadline?.toString() ?? ""),
        end: addHours(parseISO(delivery.carDeliveryDeadline?.toString() ?? ""), 2),
        type: delivery.type,
        address: address ?? "pas scpécifié",
      };
    });
  }, []);

  const { data, isLoading, error, refetch } = useFindAllDeliveryQuery(
    {
      GetAllInput: {
        pagination: { page: 0, perPage: 1000 },
        filter: {
          status: {
            in: statusFilter,
          },
          organization: {
            slug: organizationId,
          },
          carDeliveryDeadline: {
            gte: range.start,
            lte: range.end,
          },
        },
        include: {
          toAddress: true,
          toReturnToAddress: true,
          fromAddress: true,
          toReturnFromAddress: true,
        },
      },
    },
    {
      select: selectDeliveries,
    }
  );

  const handleRangeChange = React.useCallback((rawRange: { start?: Date; end?: Date } | Date[]) => {
    let start: Date;
    let end: Date;

    if (Array.isArray(rawRange)) {
      start = startOfDay(rawRange[0]);
      const lastIdx = rawRange.length - 1;
      end = rawRange.length > 1 ? endOfDay(rawRange[lastIdx]) : endOfDay(rawRange[0]);
    } else {
      start = startOfDay(rawRange.start as Date);
      end = endOfDay(rawRange.end as Date);
    }

    setRange({ start, end });
  }, []);
  const options = [
    { value: false, label: t("calendar.planned"), color: "#1D9E75" },
    { value: true, label: t("calendar.completed"), color: "#717171" },
  ];
  return (
    <Container maxWidth="xl" sx={{ padding: 4 }}>
      <Grid container spacing={4}>
        <Grid size={{ xs: 12 }}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Box flexDirection="row" display="flex" alignItems="center" gap={1}>
                <StarBorderPurple500Outlined />
                <Typography variant="h6">{t("calendar.accordion-one.title")}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                <i>{t("calendar.accordion-one.paragragh-one")}</i>
                <br />
                <i>{t("calendar.accordion-one.paragragh-two")}</i>
                <br />
                <br />
                {t("calendar.accordion-one.paragragh-three")}
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2-content"
              id="panel2-header"
            >
              <Box flexDirection="row" display="flex" alignItems="center" gap={1}>
                <InfoOutlined />
                <Typography variant="h6">{t("calendar.accordion-two.title")}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" component="div">
                {t("calendar.accordion-two.paragragh-one")}
                <ul
                  style={{
                    listStyle: "outside",
                    padding: 0,
                    margin: 0,
                    marginLeft: 20,
                    marginTop: 10,
                    marginBottom: 10,
                  }}
                >
                  <li style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 24,
                        height: 24,
                        backgroundColor: getDeliveryTypeColor(DeliveryType.STANDARD),
                        borderRadius: "50%",
                      }}
                    ></span>
                    <span>{t("calendar.accordion-two.paragragh-two")}</span>
                  </li>
                  <li style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 24,
                        height: 24,
                        backgroundColor: getDeliveryTypeColor(DeliveryType.TWO_WAY_DELIVERY),
                        borderRadius: "50%",
                      }}
                    ></span>
                    {t("calendar.accordion-two.paragragh-three")}
                  </li>
                </ul>{" "}
                {t("calendar.accordion-two.paragragh-four")}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: "inline-flex",
              p: "4px",
              borderRadius: 2,
              bgcolor: "action.hover",
              border: "0.5px solid",
              borderColor: "divider",
            }}
          >
            {options.map(({ value, label, color }) => (
              <Box
                key={String(value)}
                onClick={() => setShowDelivered(value)}
                sx={{
                  px: 2,
                  py: "7px",
                  borderRadius: "8px",
                  fontSize: 13,
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: "7px",
                  fontWeight: showDelivered === value ? 500 : 400,
                  color: showDelivered === value ? "text.primary" : "text.secondary",
                  bgcolor: showDelivered === value ? "background.paper" : "transparent",
                  border: "0.5px solid",
                  borderColor: showDelivered === value ? "divider" : "transparent",
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
                {label}
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          {error !== null && error !== undefined && (
            <ErrorAlert
              error={error}
              onRetry={() => {
                void refetch();
              }}
            />
          )}
          <Box sx={{ position: "relative" }}>
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  borderRadius: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            {error === null || error === undefined ? (
              <ClientCalendar data={data ?? []} handleRangeChange={handleRangeChange} />
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default CalendarView;
